import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Placeholder from "@tiptap/extension-placeholder"

export interface RichTextEditorProps {
  initialValue: string
  onChange: (html: string) => void
  placeholder?: string
  disabled?: boolean
}

interface ToolbarButton {
  label: string
  isActive: (editor: Editor) => boolean
  action: (editor: Editor) => void
  icon: ToolbarIconName
}

type ToolbarIconName =
  | "bold"
  | "italic"
  | "underline"
  | "heading-2"
  | "heading-3"
  | "bullet-list"
  | "ordered-list"
  | "blockquote"
  | "code-block"

const BUTTONS: ToolbarButton[] = [
  {
    label: "Bold",
    isActive: (editor) => editor.isActive("bold"),
    action: (editor) => editor.chain().focus().toggleBold().run(),
    icon: "bold",
  },
  {
    label: "Italic",
    isActive: (editor) => editor.isActive("italic"),
    action: (editor) => editor.chain().focus().toggleItalic().run(),
    icon: "italic",
  },
  {
    label: "Underline",
    isActive: (editor) => editor.isActive("underline"),
    action: (editor) => editor.chain().focus().toggleUnderline().run(),
    icon: "underline",
  },
  {
    label: "Heading 2",
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
    action: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    icon: "heading-2",
  },
  {
    label: "Heading 3",
    isActive: (editor) => editor.isActive("heading", { level: 3 }),
    action: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    icon: "heading-3",
  },
  {
    label: "Bullet list",
    isActive: (editor) => editor.isActive("bulletList"),
    action: (editor) => editor.chain().focus().toggleBulletList().run(),
    icon: "bullet-list",
  },
  {
    label: "Ordered list",
    isActive: (editor) => editor.isActive("orderedList"),
    action: (editor) => editor.chain().focus().toggleOrderedList().run(),
    icon: "ordered-list",
  },
  {
    label: "Blockquote",
    isActive: (editor) => editor.isActive("blockquote"),
    action: (editor) => editor.chain().focus().toggleBlockquote().run(),
    icon: "blockquote",
  },
  {
    label: "Code block",
    isActive: (editor) => editor.isActive("codeBlock"),
    action: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    icon: "code-block",
  },
]

function ToolbarIcon({ name }: { name: ToolbarIconName }) {
  const paths: Record<ToolbarIconName, string> = {
    bold: "M6.75 3.744h-.753v8.25h7.125a4.125 4.125 0 0 0 0-8.25H6.75Zm0 0v.38m0 16.122h6.747a4.5 4.5 0 0 0 0-9.001h-7.5v9h.753Zm0 0v-.37m0-15.751h6a3.75 3.75 0 1 1 0 7.5h-6m0-7.5v7.5m0 0v8.25m0-8.25h6.375a4.125 4.125 0 0 1 0 8.25H6.75m.747-15.38h4.875a3.375 3.375 0 0 1 0 6.75H7.497v-6.75Zm0 7.5h5.25a3.75 3.75 0 0 1 0 7.5h-5.25v-7.5Z",
    italic: "M5.248 20.246H9.05m0 0h3.696m-3.696 0 5.893-16.502m0 0h-3.697m3.697 0h3.803",
    underline: "M17.995 3.744v7.5a6 6 0 1 1-12 0v-7.5m-2.25 16.502h16.5",
    "heading-2": "M21.75 19.5H16.5v-1.609a2.25 2.25 0 0 1 1.244-2.012l2.89-1.445c.651-.326 1.116-.955 1.116-1.683 0-.498-.04-.987-.118-1.463-.135-.825-.835-1.422-1.668-1.489a15.202 15.202 0 0 0-3.464.12M2.243 4.492v7.5m0 0v7.502m0-7.501h10.5m0-7.5v7.5m0 0v7.501",
    "heading-3": "M20.905 14.626a4.52 4.52 0 0 1 .738 3.603c-.154.695-.794 1.143-1.504 1.208a15.194 15.194 0 0 1-3.639-.104m4.405-4.707a4.52 4.52 0 0 0 .738-3.603c-.154-.696-.794-1.144-1.504-1.209a15.19 15.19 0 0 0-3.639.104m4.405 4.708H18M2.243 4.493v7.5m0 0v7.502m0-7.501h10.5m0-7.5v7.5m0 0v7.501",
    "bullet-list": "M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z",
    "ordered-list": "M8.242 5.992h12m-12 6.003H20.24m-12 5.999h12M4.117 7.495v-3.75H2.99m1.125 3.75H2.99m1.125 0H5.24m-1.92 2.577a1.125 1.125 0 1 1 1.591 1.59l-1.83 1.83h2.16M2.99 15.745h1.125a1.125 1.125 0 0 1 0 2.25H3.74m0-.002h.375a1.125 1.125 0 0 1 0 2.25H2.99",
    blockquote: "M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z",
    "code-block": "M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5",
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  )
}

export function RichTextEditor({
  initialValue,
  onChange,
  placeholder = "Write the newsletter…",
  disabled = false,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder }),
    ],
    content: initialValue,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  return (
    <div
      className={`admin-richtext rounded-xl border border-gold-500 bg-white transition-colors focus-within:border-forest-700 ${
        disabled ? "opacity-50" : "hover:border-forest-700"
      }`}
    >
      <div className="admin-richtext__toolbar" role="toolbar" aria-label="Text formatting">
        {BUTTONS.map((button, index) => {
          const active = editor ? button.isActive(editor) : false
          return (
            <span key={button.label} className="contents">
              {[3, 5, 8].includes(index) && <span className="admin-richtext__divider" aria-hidden="true" />}
              <button
                type="button"
                title={button.label}
                aria-label={button.label}
                aria-pressed={active}
                disabled={disabled || !editor}
                onClick={() => button.action(editor!)}
                className="admin-richtext__button"
                data-active={active}
              >
                <ToolbarIcon name={button.icon} />
              </button>
            </span>
          )
        })}
      </div>
      <div className="admin-richtext__content">
        <EditorContent editor={editor} className="tiptap" />
      </div>
    </div>
  )
}
